import React, { ReactElement, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, TextField, IconButton, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Delete, Edit, Save, Cancel,
} from '@material-ui/icons';
import clsx from 'clsx';
import {
  DealStageHistory, dealStageOptions, DealStageType,
} from '../../../types/deals/deals';
import { formatDateWithoutTimeZoneDifference, formatDate } from '../../../formatters/dateFormatters/dateFormatters';
import { useViewDealStyles } from '../../../styles/useViewDealStyles';
import { useViewLeaseStyles } from '../../../styles/useViewLeaseStyles';
import useDeleteModal from '../../modals/DeleteModal/hooks';
import {
  useGetDealStageHistoryQuery,
  useUpdateDealStageHistoryMutation,
  useDeleteDealStageHistoryMutation,
} from '../../../queries/deals/stageHistory';
import { accessToken } from '../../../models/security';
import ComponentLoader from '../../ui/ComponentLoader';

interface DealHistoryManagerProps {
  dealId: number;
  currentStage?: string;
}

const DealHistoryManager: React.FC<DealHistoryManagerProps> = ({
  dealId, currentStage,
}): ReactElement => {
  const dealClasses = useViewDealStyles();
  const leaseClasses = useViewLeaseStyles();
  const dealStage = new DealStageType(dealStageOptions);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDate, setEditingDate] = useState<string>('');
  const [editingStage, setEditingStage] = useState<string>('');

  const { showModal, RenderModal, modalId } = useDeleteModal();

  // API hooks
  const { data: stageHistory, isLoading, error } = useGetDealStageHistoryQuery(dealId);
  const updateMutation = useUpdateDealStageHistoryMutation();
  const deleteMutation = useDeleteDealStageHistoryMutation();

  // Check if user is admin
  const isAdmin = accessToken.isCurrentRoleAdmin();

  const isCurrentStage = (stage: string) => currentStage === stage;

  const handleEdit = (historyItem: DealStageHistory) => {
    setEditingId(historyItem.id);
    setEditingDate(formatDate(historyItem.stageChangeDate));
    setEditingStage(historyItem.stage);
  };

  const handleSave = async (historyItem: DealStageHistory) => {
    const updateData = {
      stage: isCurrentStage(historyItem.stage) ? historyItem.stage : editingStage,
      stageChangeDate: editingDate || formatDate(historyItem.stageChangeDate),
    };

    try {
      await updateMutation.mutateAsync({
        dealId,
        historyId: historyItem.id,
        data: updateData,
      });

      setEditingId(null);
      setEditingDate('');
      setEditingStage('');
    } catch (err) {
      // Error handling - mutation will handle the error state
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingDate('');
    setEditingStage('');
  };

  const handleDelete = async (historyId: number | null) => {
    if (!historyId) return;

    try {
      await deleteMutation.mutateAsync({
        dealId,
        historyId,
      });
    } catch (err) {
      // Error handling - mutation will handle the error state
    }
  };

  if (isLoading) {
    return <ComponentLoader isOpen />;
  }

  if (error) {
    return (
      <Box className={dealClasses.abstractRoundBorderedCard}>
        <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
          DEAL STAGES TIMELINE
        </Typography>
        <Box className={dealClasses.noCommentsContainer}>
          <Typography variant="inherit" color="error">
            Failed to load stage history
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!stageHistory || stageHistory.length === 0) {
    return (
      <Box className={dealClasses.abstractRoundBorderedCard}>
        <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
          DEAL STAGES TIMELINE
        </Typography>
        <Box className={dealClasses.noCommentsContainer}>
          <Typography variant="inherit">No stage changes recorded yet</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className={dealClasses.abstractRoundBorderedCard}>
        <Typography variant="overline" display="block" className={leaseClasses.abstractTitles}>
          DEAL STAGES TIMELINE
        </Typography>
        <Box style={{ padding: '16px 0' }}>
          {stageHistory.map((historyItem, index) => (
            <Box
              key={historyItem.id}
              style={{
                display: 'flex',
                marginBottom: '24px',
                opacity: updateMutation.isLoading || deleteMutation.isLoading ? 0.7 : 1,
              }}
            >
              <Box style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: '16px',
                minWidth: '24px',
              }}
              >
                <Box
                  style={{
                    width: isCurrentStage(historyItem.stage) ? '20px' : '16px',
                    height: isCurrentStage(historyItem.stage) ? '20px' : '16px',
                    borderRadius: '50%',
                    backgroundColor: dealStage.getColorByKey(historyItem.stage),
                    border: isCurrentStage(historyItem.stage) ? '3px solid #4051B5' : 'none',
                    boxShadow: isCurrentStage(historyItem.stage) ? '0 0 0 2px rgba(64, 81, 181, 0.2)' : 'none',
                    transition: 'all 0.2s ease-in-out',
                    marginBottom: '8px',
                  }}
                />
                {index < stageHistory.length - 1 && (
                  <Box style={{
                    width: '2px',
                    height: '40px',
                    backgroundColor: '#e0e0e0',
                    flexGrow: 1,
                  }}
                  />
                )}
              </Box>
              <Box style={{ flex: 1 }}>
                <Box className={clsx(leaseClasses.spaceBetween, leaseClasses.abstractInfoRow)}>
                  <Typography
                    variant="h6"
                    component="span"
                    style={{
                      fontWeight: isCurrentStage(historyItem.stage) ? 'bolder' : 'bold',
                      color: isCurrentStage(historyItem.stage) ? '#4051B5' : 'inherit',
                    }}
                  >
                    {dealStage.getDisplayNameByKey(historyItem.stage)}
                    {isCurrentStage(historyItem.stage) && (
                      <Typography
                        component="span"
                        style={{
                          fontSize: '12px',
                          color: '#4051B5',
                          fontWeight: 'normal',
                          marginLeft: '8px',
                        }}
                      >
                        (Current)
                      </Typography>
                    )}
                  </Typography>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {editingId === historyItem.id ? (
                      <>
                        {!isCurrentStage(historyItem.stage) && (
                          <FormControl size="small" variant="outlined" style={{ minWidth: 120, marginRight: '8px' }}>
                            <InputLabel>Stage</InputLabel>
                            <Select
                              value={editingStage}
                              onChange={(e) => setEditingStage(e.target.value as string)}
                              label="Stage"
                            >
                              {dealStage.getDropdownOptions().map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.displayValue}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <TextField
                          type="date"
                          value={editingDate}
                          onChange={(e) => setEditingDate(e.target.value)}
                          size="small"
                          variant="outlined"
                          style={{ marginRight: '8px' }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleSave(historyItem)}
                          color="primary"
                          disabled={updateMutation.isLoading}
                        >
                          <Save />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancel}
                          color="secondary"
                          disabled={updateMutation.isLoading}
                        >
                          <Cancel />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {isAdmin && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(historyItem)}
                              disabled={updateMutation.isLoading || deleteMutation.isLoading}
                            >
                              <Edit />
                            </IconButton>
                            {!isCurrentStage(historyItem.stage) && (
                              <IconButton
                                size="small"
                                onClick={() => showModal(historyItem.id)}
                                style={{ color: '#E20101' }}
                                disabled={updateMutation.isLoading || deleteMutation.isLoading}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
                <Box className={leaseClasses.abstractInfoRow}>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Changed by:
                    {' '}
                    {historyItem.user ? historyItem.user.fullName : 'System'}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#666' }}>
                    {formatDateWithoutTimeZoneDifference(historyItem.stageChangeDate)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <RenderModal
        body="Are you sure you want to delete this stage change record?"
        confirmAction={() => handleDelete(modalId)}
        title="Delete Stage Change"
      />
    </>
  );
};

DealHistoryManager.propTypes = {
  dealId: PropTypes.number.isRequired,
  currentStage: PropTypes.string.isRequired,
};

export default DealHistoryManager;
